/* global $ */
import Sidebar from "@/components/Home_Sidebar/sidebarView.vue";
import Navbar from "@/components/Home_Navbar/NavbarView.vue";
import PoweredBy from '@/components/PoweredBy/PoweredByView.vue';
import { get_token, getCookie } from '@/router/index.js';
import FilePreviewComponent from "@/components/FilePreviewComponent/FilePreviewComponent.vue";
import EmojiPicker from 'vue3-emoji-picker'
import 'vue3-emoji-picker/css'

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮'];

export default {
  name: "OneChatView",
  components: { Sidebar, Navbar, FilePreviewComponent, EmojiPicker, PoweredBy },

  data: () => ({
    csrfToken: '',

    // Conversations
    conversations: [],
    active_conversation_id: null,
    conversations_loading: true,
    conversations_loaded: false,

    // Messages
    messages: [],
    messages_loading: false,

    // Message input
    draft: "",
    sending_message: false,
    selected_file: null,
    selected_file_preview_url: null,

    // Reply / quote
    reply_to_message: null,

    // New conversation modal
    show_new_conversation_modal: false,
    new_conversation_user_id: "",
    new_conversation_message: "",
    starting_conversation: false,
    available_users: [],
    users_loading: false,

    // Current user
    current_user_id: null,
    current_user: null,

    // Polling
    poll_interval: null,
    poll_frequency: 5000,

    // Search
    search_query: '',
    contact_search_query: '',

    // Attachment preview
    preview_modal: false,
    preview_file_name: "",
    preview_file_url: "",
    preview_file_access_level: null,

    // Edit
    editing_message_key: null,
    edit_draft: '',
    saving_edit: false,

    // Delete
    pending_delete_item: null,
    deleting_message: false,

    // Pin
    pinned_messages: [],
    show_pinned_panel: false,
    pinning_in_progress: false,

    // Toolbar hover (desktop)
    hovered_message_key: null,

    // Emoji picker
    quick_emojis: QUICK_EMOJIS,
    emoji_picker_key: null,
    reacting_message_id: null,

    // More menu
    active_toolbar_key: null,
    toolbar_menu_direction: {},

    // Scroll control
    user_scrolled_up: false,
    new_messages_banner: false,

    // Info panel
    show_info_panel: false,
    info_tab: 'profile',

    // Mobile
    mobile_action_item: null,

    // Long-press internals
    _long_press_timer: null,
    _touch_moved: false,
    _touch_start_x: 0,
    _touch_start_y: 0,

    // In-conversation message search
    show_message_search: false,
    message_search_query: '',
    message_search_index: 0,
  }),

  computed: {

    filtered_conversations() {
      let visible = this.conversations.filter(conv => {
        const has_messages = conv.last_message !== null;
        const is_currently_active = conv.id === this.active_conversation_id;
        return has_messages || is_currently_active;
      });
      if (!this.search_query) return visible;
      const query = this.search_query.toLowerCase();
      return visible.filter(conv => {
        const other_user = this.get_other_user(conv);
        const name = other_user.name.toLowerCase();
        const last_message = conv.last_message?.content?.toLowerCase() || '';
        return name.includes(query) || last_message.includes(query);
      });
    },

    rendered_stream() {
      const out = [];
      let last_date = null;
      this.messages.forEach((message, indx) => {
        if (message.kind === "unread") {
          out.push({ kind: "unread", _k: `unread-${indx}` });
          return;
        }
        const is_me = message.sender.id === this.current_user_id;
        const date_str = this.format_date(message.created_at);
        if (date_str !== last_date) {
          out.push({ kind: "stamp", when: date_str, _k: `date-${indx}` });
          last_date = date_str;
        }
        out.push({
          kind: "msg",
          id: message.id,
          side: is_me ? "right" : "left",
          text: message.content,
          attachments: message.attachments || [],
          timestamp: this.format_time(message.created_at),
          edited_at: message.edited_at,
          is_pinned: message.is_pinned,
          reactions: message.reactions || [],
          reply_to: message.reply_to || null,
          _k: `msg-${message.id}`,
        });
      });
      return out;
    },

    active_conversation() {
      return this.conversations.find(c => c.id === this.active_conversation_id);
    },

    filtered_users() {
      const users = this.available_users.filter(u => u.id !== this.current_user_id);
      if (!this.contact_search_query) return users;
      const query = this.contact_search_query.toLowerCase();
      return users.filter(user => {
        const full_name = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        return full_name.includes(query) || email.includes(query) || username.includes(query);
      });
    },

    shared_files() {
      const files = [];
      for (const msg of this.messages) {
        if (msg.attachments && msg.attachments.length) {
          for (const f of msg.attachments) {
            files.push({ ...f, created_at: msg.created_at });
          }
        }
      }
      return files;
    },

    shared_files_count() {
      return this.shared_files.length;
    },

    message_search_matches() {
      const q = this.message_search_query.trim().toLowerCase();
      if (!q) return [];
      return this.rendered_stream
        .filter(item => item.kind === 'msg' && item.text && item.text.toLowerCase().includes(q))
        .map(item => item._k);
    },
  },

  mounted() {
    this.csrfToken = getCookie('csrftoken');
    this.load_initial_data();
    this.attach_modal_listener();
    document.addEventListener('click', this._close_popups_on_outside);
  },

  beforeUnmount() {
    this.stop_polling();
    this.detach_scroll_listener();
    this.revoke_preview_url();
    if (this._long_press_timer) clearTimeout(this._long_press_timer);
    document.removeEventListener('click', this._close_popups_on_outside);
    $('.modal-backdrop').remove();
    $('body').removeClass('modal-open').css('padding-right', '');
  },

  methods: {

    // ── Scroll ────────────────────────────────────
    attach_scroll_listener() {
      this.$nextTick(() => {
        const el = this.$refs.scroll_area;
        if (!el) return;
        el.removeEventListener('scroll', this._scroll_handler);
        this._scroll_handler = () => {
          const area = this.$refs.scroll_area;
          if (!area) return;
          const distance = area.scrollHeight - area.scrollTop - area.clientHeight;
          this.user_scrolled_up = distance > 100;
          if (!this.user_scrolled_up) {
              this.new_messages_banner = false;
          }
        };
        el.addEventListener('scroll', this._scroll_handler, { passive: true });
      });
    },

    detach_scroll_listener() {
      const el = this.$refs.scroll_area;
      if (el && this._scroll_handler) {
        el.removeEventListener('scroll', this._scroll_handler);
      }
    },

    scroll_to_bottom() {
      this.$nextTick(() => {
        const el = this.$refs.scroll_area;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    jump_to_bottom() {
      this.user_scrolled_up    = false;
      this.new_messages_banner = false;
      this.scroll_to_bottom();
    },

    // ── Auth helpers ──────────────────────────────
    require_token_or_redirect() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return null;
      }
      return token;
    },

    _auth_headers(token) {
      return ({
        'Authorization': `Token ${token}`,
        'X-CSRFToken':   this.csrfToken,
        'X-Page-URL':    window.location.href,
      });
    },

    attach_modal_listener() {
      this.$nextTick(() => {
        const $modal = $('#newConversationModal');
        $modal.off('hidden.bs.modal shown.bs.modal show.bs.modal');
        $modal.on('show.bs.modal', () => {
          $('.modal-backdrop').remove();
          $('body').removeClass('modal-open');
          $('body').css('padding-right', '');
          $('body').css('overflow', '');
        });
        $modal.on('hidden.bs.modal', () => {
          this.new_conversation_user_id = '';
          this.new_conversation_message = '';
          this.contact_search_query = '';
          this.starting_conversation = false;
          $('.modal-backdrop').remove();
          $('body').removeClass('modal-open');
          $('body').css('padding-right', '');
          $('body').css('overflow', '');
        });
      });
    },

    get_sender_name(sender) {
      if (!sender) return 'Unknown';
      const full_name = `${sender.first_name || ''} ${sender.last_name || ''}`.trim();
      return full_name || sender.username || 'Unknown';
    },

    get_other_user(conversation) {
      const other_participant = conversation?.participants?.find(
        p => p.user.id !== this.current_user_id
      );
      const user = other_participant?.user;
      const name = user
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
        : 'Unknown';
      const avatar = user ? user.avatar || `https://i.pravatar.cc/40?u=${user.id}` : null;
      return ({ name, avatar });
    },

    format_time(datetime_string) {
      if (!datetime_string) return '';
      const date = new Date(datetime_string);
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },

    format_date(datetime_string) {
      if (!datetime_string) return '';
      const date = new Date(datetime_string);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return 'Today';
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    },

    format_time_ago(datetime_string) {
      if (!datetime_string) return '';
      const date = new Date(datetime_string);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    },

    auto_resize_textarea(event) {
      const textarea = event.target;
      textarea.style.height = 'auto';
      const computed_style = window.getComputedStyle(textarea);
      const max_height = parseInt(computed_style.maxHeight, 10);
      const min_height = parseInt(computed_style.minHeight, 10) || 40;
      const new_height = Math.min(Math.max(textarea.scrollHeight, min_height), max_height);
      textarea.style.height = new_height + 'px';
    },

    close_modal() { $('#newConversationModal').modal('hide'); },

    truncate_text(text, length = 30) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    is_image_file(file_name) {
      if (!file_name) return false;
      const ext = file_name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
    },

    is_video_file(file_name) {
      if (!file_name) return false;
      const ext = file_name.split('.').pop().toLowerCase();
      return ['mp4', 'webm', 'ogg', 'ogv', 'mov'].includes(ext);
    },

    is_audio_file(file_name) {
      if (!file_name) return false;
      const ext = file_name.split('.').pop().toLowerCase();
      return ['mp3', 'wav', 'ogg', 'oga', 'aac', 'm4a', 'flac', 'opus', 'weba'].includes(ext);
    },

    stop_other_media(event) {
      const current = event.target;
      const scroll_el = this.$refs.scroll_area;
      if (!scroll_el) return;
      scroll_el.querySelectorAll('audio, video').forEach(el => {
        if (el !== current && !el.paused) {
          el.pause();
        }
      });
    },

    file_icon_chat(file_name) {
      const ext = (file_name || '').split('.').pop().toLowerCase();
      if (['jpg','jpeg','png','webp','gif','svg'].includes(ext)) return "far fa-file-image text-success";
      if (ext === "pdf") return "far fa-file-pdf text-danger";
      if (['doc','docx'].includes(ext)) return "far fa-file-word text-primary";
      if (['mp4','webm','ogg','ogv','mov'].includes(ext)) return "fas fa-film text-warning";
      if (['mp3','wav','aac','m4a','flac','opus'].includes(ext)) return "fas fa-music text-info";
      return "far fa-file-alt text-secondary";
    },

    preview_chat_file(file) {
      this.preview_file_name = file.file_name;
      this.preview_file_url = file.file_url;
      this.preview_file_access_level = null;
      this.preview_modal = true;
    },

    close_the_file_preview() {
      this.preview_modal = false;
      this.preview_file_url = null;
    },

    handle_file_select(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.selected_file = file;
      this.revoke_preview_url();
      if (this.is_image_file(file.name)) {
        this.selected_file_preview_url = URL.createObjectURL(file);
      } else {
        this.selected_file_preview_url = null;
      }
    },

    remove_selected_file() {
      this.selected_file = null;
      this.revoke_preview_url();
      if (this.$refs.file_input) this.$refs.file_input.value = '';
    },

    revoke_preview_url() {
      if (this.selected_file_preview_url) {
        URL.revokeObjectURL(this.selected_file_preview_url);
        this.selected_file_preview_url = null;
      }
    },

    get_initials(name) {
      if (!name || name === 'Unknown') return '?';
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    get_avatar_class(name) {
      const palette = ['oc-av-0','oc-av-1','oc-av-2','oc-av-3','oc-av-4','oc-av-5','oc-av-6','oc-av-7','oc-av-8','oc-av-9'];
      if (!name || name === 'Unknown') return 'oc-av-unknown';
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return palette[Math.abs(hash) % palette.length];
    },

    get_avatar_style(name) {
      const palette = ['#C8102E','#1a73e8','#0f9d58','#e52592','#f29900','#7627bb','#12b5cb','#e65100','#00796b','#4285f4'];
      if (!name || name === 'Unknown') return { background: '#9E9890', color: '#fff' };
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return ({ background: palette[Math.abs(hash) % palette.length], color: '#fff' });
    },

    get_sender_name_by_id(msg_id) {
      const msg = this.messages.find(m => m.id === msg_id);
      if (!msg || !msg.sender) return 'Unknown';
      return `${msg.sender.first_name || ''} ${msg.sender.last_name || ''}`.trim() || msg.sender.username || 'Unknown';
    },

    open_info_panel(tab = 'pinned') {
      this.info_tab        = tab;
      this.show_info_panel = true;
    },

    handle_touch_start(item, event) {
      this._touch_moved   = false;
      this._touch_start_x = event.touches[0].clientX;
      this._touch_start_y = event.touches[0].clientY;
      if (this._long_press_timer) clearTimeout(this._long_press_timer);
      this._long_press_timer = setTimeout(() => {
        if (!this._touch_moved) {
          if (navigator.vibrate) navigator.vibrate(40);
          this.mobile_action_item = item;
        }
      }, 500);
    },

    handle_touch_end() {
      if (this._long_press_timer) {
        clearTimeout(this._long_press_timer);
        this._long_press_timer = null;
      }
    },

    handle_touch_move(event) {
      if (this._long_press_timer) {
        const dx = Math.abs(event.touches[0].clientX - this._touch_start_x);
        const dy = Math.abs(event.touches[0].clientY - this._touch_start_y);
        if (dx > 8 || dy > 8) {
          this._touch_moved = true;
          clearTimeout(this._long_press_timer);
          this._long_press_timer = null;
        }
      }
    },

    handle_bubble_click(item) {
      if (this.active_toolbar_key === item._k) this.active_toolbar_key = null;
      if (this.emoji_picker_key === item._k) this.emoji_picker_key = null;
    },

    _close_popups_on_outside(e) {
      if (
        this.emoji_picker_key &&
        !e.target.closest('.emoji-picker-popup') &&
        !e.target.closest('.msg-tb-btn-emoji')
      ) {
        this.emoji_picker_key = null;
      }

      if (
        this.active_toolbar_key &&
        !e.target.closest('.msg-more-menu') &&
        !e.target.closest('.msg-tb-btn-dots')
      ) {
        this.active_toolbar_key = null;
      }
    },

    toggle_emoji_picker(item, event) {
      event.stopPropagation();
      this.emoji_picker_key = this.emoji_picker_key === item._k ? null : item._k;
    },

    toggle_more_menu(item, event) {
      event.stopPropagation();

      if (this.active_toolbar_key === item._k) {
        this.active_toolbar_key = null;
        return;
      }
      const btn = event.currentTarget;
      const btn_rect = btn.getBoundingClientRect();
      const scroll_el = this.$refs.scroll_area;
      let direction = 'up';

      if (scroll_el) {
        const scroll_rect = scroll_el.getBoundingClientRect();
        const space_above = btn_rect.top - scroll_rect.top;
        if (space_above < 150) direction = 'down';
      }
      this.toolbar_menu_direction = {
        ...this.toolbar_menu_direction,
        [item._k]: direction,
      };

      this.active_toolbar_key = item._k;
    },

    my_reaction_for(item) {
      if (!item.reactions || !item.reactions.length) return null;
      const r = item.reactions.find(rg => rg.reacted_by_me);
      return r ? r.emoji : null;
    },

    async send_reaction(item, emoji, event) {
      if (event) event.stopPropagation();
      if (this.reacting_message_id === item.id) return;

      const token = this.require_token_or_redirect();
      if (!token) return;

      const current = this.my_reaction_for(item);
      const emoji_to_send = current === emoji ? '' : emoji;

      this.reacting_message_id = item.id;
      this.emoji_picker_key = null;

      try {
        const data = await $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/toggle_reaction/`,
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({ message_id: item.id, emoji: emoji_to_send }),
          xhrFields: { withCredentials: true },
          headers: this._auth_headers(token),
        });

        if (data?.status === 'success') {
          const idx = this.messages.findIndex(m => m.id === item.id);
          if (idx !== -1) {
            const current_message = Reflect.get(this.messages, idx);
            this.messages.splice(idx, 1, {
              ...current_message,
              reactions: data.data.reactions,
            });
          }
        }
      } catch (err) {
        this.$notyf.error('Failed to react to message');
      } finally {
        this.reacting_message_id = null;
      }
    },

    set_reply_to(item) {
      const sender_name = this.get_sender_name_by_id(item.id);
      this.reply_to_message = {
        id: item.id,
        sender_name,
        content: item.text || '',
        attachments: item.attachments || [],
      };
      this.$nextTick(() => {
        const ta = this.$el.querySelector('.oc-textarea');
        if (ta) ta.focus();
      });
    },

    clear_reply_to() {
      this.reply_to_message = null;
    },

    // ── Data loading ──────────────────────────────
    async load_initial_data() {
      await this.load_current_user();
      await this.load_conversations();
      await this.load_available_users();
      this.start_polling();
    },

    load_current_user() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      return $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_current_user/`,
        method: 'GET',
        xhrFields: { withCredentials: true },
        headers: { 'Content-Type': 'application/json', ...this._auth_headers(token) },
      }).then(data => {
        if (data?.status === 'success') {
          this.current_user = data.data;
          this.current_user_id = data.data.id;
        } else {
          this.$notyf.error(data?.message || 'Failed to load user');
        }
      }).catch(jq => {
        this.$notyf.error(jq?.responseJSON?.message || `Error loading user: HTTP ${jq.status}`);
      });
    },

    load_conversations(is_silent = false) {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      if (!is_silent) this.conversations_loading = true;
      return $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/get_user_conversations/`,
        method: 'GET',
        xhrFields: { withCredentials: true },
        headers: this._auth_headers(token),
      }).then(data => {
        if (data?.status === 'success') this.conversations = data.data || [];
      }).always(() => {
        this.conversations_loading = false;
        this.conversations_loaded = true;
      });
    },

    load_available_users() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      this.users_loading = true;
      return $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/aic_drive/get_available_users/`,
        method: 'GET',
        xhrFields: { withCredentials: true },
        headers: { 'Content-Type': 'application/json', ...this._auth_headers(token) },
      }).then(data => {
        this.available_users = data?.status === 'success' ? data.data || [] : [];
      }).catch(jq => {
        console.error('Error loading users:', jq);
        this.available_users = [];
      }).always(() => {
        this.users_loading = false;
      });
    },

    select_conversation(conversation_id) {
      this.active_conversation_id = conversation_id;
      this.messages = [];
      this.pinned_messages = [];
      this.show_pinned_panel = false;
      this.user_scrolled_up = false;
      this.new_messages_banner = false;
      this.show_info_panel = false;
      this.info_tab = 'profile';
      this.active_toolbar_key = null;
      this.mobile_action_item = null;
      this.emoji_picker_key = null;
      this.reply_to_message = null;
      this.show_message_search  = false;
      this.message_search_query = '';
      this.message_search_index = 0;
      this.cancel_edit();
      this.load_messages(conversation_id);
      this.load_pinned_messages(conversation_id);
    },

    load_messages(conversation_id, is_silent = false) {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      if (!is_silent) this.messages_loading = true;

      return $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/get_conversation_messages/`,
        method: 'POST',
        data: JSON.stringify({ conversation_id, limit: 50, offset: 0 }),
        contentType: 'application/json',
        xhrFields: { withCredentials: true },
        headers: this._auth_headers(token),
      }).then(data => {
        if (data?.status !== 'success') return;
        const new_messages = data.data.messages || [];

        if (!is_silent) {
          this.messages = new_messages;
          this.$nextTick(() => {
            this.scroll_to_bottom();
            this.attach_scroll_listener();
            this.mark_as_read(conversation_id);
          });
          return;
        }

        this._apply_silent_poll(new_messages, conversation_id);
      }).always(() => {
        this.messages_loading = false;
      });
    },

    _apply_silent_poll(new_messages, conversation_id) {
      const old_last_id = this.messages.length ? Math.max(...this.messages.map(m => m.id || 0)) : 0;
      const new_last_id = new_messages.length ? Math.max(...new_messages.map(m => m.id || 0)) : 0;

      const last_message = this.messages.at(-1);

      const has_new_messages =
        this.messages.length !== new_messages.length ||
        (this.messages.length > 0 && last_message && last_message.id !== new_last_id);

      const has_content_updates =
        !has_new_messages &&
        this.messages.some((msg, i) => {
          const new_msg = new_messages.at(i);
          if (!new_msg) return false;
          return (
            msg.content !== new_msg.content ||
            msg.is_pinned !== new_msg.is_pinned ||
            msg.edited_at !== new_msg.edited_at ||
            JSON.stringify(msg.reactions) !== JSON.stringify(new_msg.reactions)
          );
        });

      if (!has_new_messages && !has_content_updates) return;

      const el = this.$refs.scroll_area;
      const prev_scroll_top = el ? el.scrollTop : 0;
      const was_at_bottom = !this.user_scrolled_up;

      this.messages = new_messages;

      this.$nextTick(() => {
        if (!el) return;
        if (has_content_updates && !has_new_messages) {
          el.scrollTop = prev_scroll_top;
          return;
        }
        if (was_at_bottom) {
          this.scroll_to_bottom();
          const incoming = new_messages.some(
            m => m.sender?.id !== this.current_user_id && m.id > old_last_id
          );
          if (incoming) this.mark_as_read(conversation_id);
        } else {
          el.scrollTop = prev_scroll_top;
          if (new_last_id > old_last_id) this.new_messages_banner = true;
        }
      });
    },

    send() {
      if ((!this.draft && !this.selected_file) || this.sending_message) return;
      const token = this.require_token_or_redirect();
      if (!token) return;

      this.sending_message = true;

      const form_data = new FormData();
      form_data.append('conversation_id', this.active_conversation_id);
      form_data.append('content', this.draft);
      if (this.selected_file) form_data.append('attachment', this.selected_file);
      if (this.reply_to_message) form_data.append('reply_to_id', this.reply_to_message.id);

      const backup_draft = this.draft;
      const backup_reply_to = this.reply_to_message;
      this.draft = "";
      this.selected_file = null;
      this.reply_to_message = null;
      this.revoke_preview_url();

      this.$nextTick(() => {
        const textarea = this.$el.querySelector('.oc-textarea');
        if (textarea) textarea.style.height = 'auto';
      });

      $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/send_message/`,
        method: 'POST',
        data: form_data,
        processData: false,
        contentType: false,
        xhrFields: { withCredentials: true },
        headers: this._auth_headers(token),
      }).then(data => {
        if (data?.status === 'success') {
          this.messages.push(data.data);
          this.user_scrolled_up    = false;
          this.new_messages_banner = false;
          this.$nextTick(this.scroll_to_bottom);
          this.load_conversations();
        } else {
          this.draft = backup_draft;
          this.reply_to_message = backup_reply_to;
          this.$notyf.error(data?.message || 'Failed to send message');
        }
      }).catch(() => {
        this.draft = backup_draft;
        this.reply_to_message = backup_reply_to;
        this.$notyf.error("Network error while sending message");
      }).always(() => {
        this.sending_message = false;
      });
    },

    mark_as_read(conversation_id) {
      if (!this.messages.length) return;
      const has_incoming = this.messages.some(m => m.sender && m.sender.id !== this.current_user_id);
      if (!has_incoming) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      const last_message = this.messages.at(-1);
      $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/mark_messages_as_read/`,
        method: 'POST',
        data: JSON.stringify({ conversation_id, last_message_id: last_message.id }),
        contentType: 'application/json',
        xhrFields: { withCredentials: true },
        headers: this._auth_headers(token),
      }).then(data => {
        if (data?.status === 'success') {
          const conv = this.conversations.find(c => c.id === conversation_id);
          if (conv) conv.unread_count = 0;
        }
      }).catch(jq => console.error('Error marking as read:', jq));
    },

    start_polling() {
      const poll = async () => {
        if (this.active_conversation_id) {
          await this.load_messages(this.active_conversation_id, true);
          await this.load_pinned_messages(this.active_conversation_id);
        }
        this.load_conversations(true);
        setTimeout(poll, this.poll_frequency);
      };
      poll();
    },

    stop_polling() {
      if (this.poll_interval) {
        clearInterval(this.poll_interval);
        this.poll_interval = null;
      }
    },

    start_new_conversation() {
      if (!this.new_conversation_user_id) { this.$notyf.info('Please select a user'); return; }
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.starting_conversation = true;

      $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/start_direct_conversation/`,
        method: 'POST',
        data: JSON.stringify({
          recipient_id:    parseInt(this.new_conversation_user_id),
          initial_message: this.new_conversation_message || '',
        }),
        contentType: 'application/json',
        xhrFields: { withCredentials: true },
        headers: this._auth_headers(token),
      }).then(data => {
        if (data?.status === 'success') {
          const conversation = data.data.conversation;
          if (!conversation.participants || conversation.participants.length === 0) {
            const other_user = this.available_users.find(
              u => u.id === parseInt(this.new_conversation_user_id, 10)
            );
            if (other_user) {
              conversation.participants = [
                { id: this.current_user_id, ...this.current_user },
                {
                  id: other_user.id,
                  first_name: other_user.first_name,
                  last_name: other_user.last_name,
                  username: other_user.username,
                  avatar: other_user.avatar
                },
              ];
            }
          }
          if (data.data.created) {
            this.conversations.unshift(conversation);
          } else {
            const index = this.conversations.findIndex(c => c.id === conversation.id);
            if (index !== -1) this.conversations.splice(index, 1, conversation);
          }
          this.$notyf.success('Conversation started successfully');
          this.select_conversation(conversation.id);

          const $modal = $('#newConversationModal');
          $modal.removeClass('show').css('display', 'none').attr('aria-hidden', 'true');
          $modal.removeAttr('aria-modal').removeAttr('role');
          $('.modal-backdrop').remove();
          $('body').removeClass('modal-open').css('padding-right', '').css('overflow', '');
          this.new_conversation_user_id = '';
          this.new_conversation_message = '';
          this.contact_search_query = '';
          $modal.removeData('bs.modal');
        } else {
          this.$notyf.error(data?.message || 'Failed to start conversation');
        }
      }).catch(jq => {
        this.$notyf.error(jq?.responseJSON?.message || `Error starting conversation: HTTP ${jq.status}`);
      }).always(() => {
        this.starting_conversation = false;
      });
    },

    start_edit(item) {
      this.editing_message_key = item._k;
      this.edit_draft          = item.text || '';
      this.$nextTick(() => {
        const ta = this.$el.querySelector('.edit-area textarea');
        if (ta) {
          // Auto-size to content
          ta.style.height = 'auto';
          ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
          ta.focus();
          // Move cursor to end
          ta.setSelectionRange(ta.value.length, ta.value.length);
        }
      });
    },

    cancel_edit() {
      this.editing_message_key = null;
      this.edit_draft          = '';
    },

    async submit_edit(item) {
      if (!this.edit_draft.trim() || this.saving_edit) return;
      const token = this.require_token_or_redirect();
      this.saving_edit = true;
      try {
        const res = await $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/edit_message/`,
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({ message_id: item.id, content: this.edit_draft.trim() }),
          xhrFields: { withCredentials: true },
          headers: this._auth_headers(token),
        });
        if (res.status === 'success' || res.id) {
          const updated_data = res.data || res;
          const current_message = this.messages.find(m => m.id === item.id);
          if (current_message) {
            const idx = this.messages.findIndex(m => m.id === item.id);
            this.messages.splice(idx, 1, {
              ...current_message, ...updated_data
            });
          }
          this.cancel_edit();
          this.$notyf.success('Message updated');
        }
      } catch (e) {
        this.$notyf.error('Failed to edit');
      } finally {
        this.saving_edit = false;
      }
    },

    confirm_delete(item) {
      this.pending_delete_item = item;
      $('#deleteMessageModal').modal('show');
    },

    async execute_delete() {
      if (!this.pending_delete_item || this.deleting_message) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.deleting_message = true;
      try {
        const data = await $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/delete_message/`,
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({ message_id: this.pending_delete_item.id }),
          xhrFields: { withCredentials: true },
          headers: this._auth_headers(token),
        });
        if (data?.status === 'success') {
          this.messages = this.messages.filter(m => m.id !== this.pending_delete_item.id);
          this.pinned_messages = this.pinned_messages.filter(m => m.id !== this.pending_delete_item.id);
          $('#deleteMessageModal').modal('hide');
          this.pending_delete_item = null;
        } else {
          this.$notyf.error(data?.message || 'Failed to delete message');
        }
      } catch (jq) {
        this.$notyf.error(jq?.responseJSON?.message || 'Network error while deleting');
      } finally {
        this.deleting_message = false;
      }
    },

    async toggle_pin(item) {
      if (this.pinning_in_progress) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.pinning_in_progress = true;

      try {
        const response_data = await $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/pin_message/`,
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({ message_id: item.id, pin: !item.is_pinned }),
          xhrFields: { withCredentials: true },
          headers: this._auth_headers(token),
        });

        if (response_data && (response_data.status === 'success' || response_data.id)) {
          const current_message = this.messages.find(m => m.id === item.id);

          if (current_message) {
            const idx = this.messages.findIndex(m => m.id === item.id);

            this.messages.splice(idx, 1, {
              ...current_message,
              is_pinned: !item.is_pinned
            });
          }
          await this.load_pinned_messages(this.active_conversation_id);
        }
      } catch (error_obj) {
        if (error_obj.status === 200 || error_obj.status === 201) {
          const idx = this.messages.findIndex(m => m.id === item.id);
          if (idx !== -1) {
            this.messages.splice(idx, 1, {
              ...this.messages[idx],
              is_pinned: !item.is_pinned
            });
          }
          await this.load_pinned_messages(this.active_conversation_id);
        } else {
          this.$notyf.error(error_obj?.responseJSON?.message || 'network_error_while_pinning');
        }
      } finally {
        this.pinning_in_progress = false;
      }
    },

    toggle_pin_by_msg(msg) {
      this.toggle_pin({ id: msg.id, is_pinned: true, _k: `msg-${msg.id}` });
    },

    async load_pinned_messages(conversation_id) {
      const token = this.require_token_or_redirect();
      if (!token) return;
      try {
        const data = await $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/one_chat_management/get_pinned_messages/`,
          method: 'POST',
          contentType: 'application/json',
          data: JSON.stringify({ conversation_id }),
          xhrFields: { withCredentials: true },
          headers: this._auth_headers(token),
        });
        if (data?.status === 'success') this.pinned_messages = data.data || [];
      } catch (jq) {
        console.error('Failed to load pinned messages:', jq);
      }
    },

    toggle_message_search() {
      this.show_message_search = !this.show_message_search;
      if (this.show_message_search) {
        this.$nextTick(() => {
          const input = this.$refs.msg_search_input;
          if (input) input.focus();
        });
      } else {
        this.message_search_query = '';
        this.message_search_index = 0;
      }
    },

    clear_message_search() {
      this.show_message_search = false;
      this.message_search_query = '';
      this.message_search_index = 0;
    },

    next_search_result() {
      if (!this.message_search_matches.length) return;
      this.message_search_index =
        (this.message_search_index + 1) % this.message_search_matches.length;
      this.scroll_to_search_result();
    },

    prev_search_result() {
      if (!this.message_search_matches.length) return;
      this.message_search_index =
        (this.message_search_index - 1 + this.message_search_matches.length) %
        this.message_search_matches.length;
      this.scroll_to_search_result();
    },

    scroll_to_search_result() {
      const target_key = this.message_search_matches[this.message_search_index];
      if (!target_key) return;
      this.$nextTick(() => {
        const scroll_el = this.$refs.scroll_area;
        if (!scroll_el) return;
        const rows = scroll_el.querySelectorAll('.message-row');
        const stream_msgs = this.rendered_stream.filter(i => i.kind === 'msg');
        const row_index = stream_msgs.findIndex(i => i._k === target_key);
        const row_el = Reflect.get(rows, row_index);
        if (row_el) {
          row_el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    highlight_text(text) {
      if (!text) return '';

      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

      const q = this.message_search_query.trim();
      if (!q) return escaped;

      const q_lower = q.toLowerCase();
      const src = escaped.toLowerCase();
      let result = '';
      let cursor = 0;

      while (cursor < escaped.length) {
        const match_pos = src.indexOf(q_lower, cursor);
        if (match_pos === -1) {
          result += escaped.slice(cursor);
          break;
        }
        result += escaped.slice(cursor, match_pos);
        const matched_slice = escaped.slice(match_pos, match_pos + q_lower.length);
        result += `<mark class="msg-search-highlight">${matched_slice}</mark>`;
        cursor = match_pos + q_lower.length;
      }

      return result;
    },
  },
};